import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Loader2, Crop as CropIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import getCroppedImg from "../lib/cropImage";

const ASPECTS = [
  { label: "Square", v: 1 },
  { label: "Portrait", v: 4 / 5 },
  { label: "Wide", v: 16 / 9 },
];

/**
 * Reusable crop modal. Mount conditionally (e.g. {src && <ImageCropperModal .../>})
 * so it resets each time a new image is picked.
 * Props: src (object URL), defaultAspect, round (circular avatar), onCancel, onCropped(blob)
 */
export default function ImageCropperModal({ src, defaultAspect = 1, round = false, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(round ? 1 : defaultAspect);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const apply = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedImg(src, pixels);
      await onCropped(blob);
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md overflow-hidden bg-white p-0 text-black" aria-describedby={undefined}>
        <DialogTitle className="flex items-center gap-2 px-5 pt-4 text-base font-bold">
          <CropIcon size={16} /> Crop image
        </DialogTitle>
        <div className="relative mt-3 h-72 w-full bg-neutral-900">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={round ? 1 : aspect}
            cropShape={round ? "round" : "rect"}
            showGrid={!round}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="space-y-3 p-5">
          {!round && (
            <div className="flex gap-2">
              {ASPECTS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => setAspect(a.v)}
                  data-testid={`crop-aspect-${a.label.toLowerCase()}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${aspect === a.v ? "border-black bg-black text-white" : "border-black/15 hover:bg-black/5"}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <span className="text-xs text-black/50">Zoom</span>
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-indigo-500" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm hover:bg-black/5">Cancel</button>
            <button type="button" onClick={apply} disabled={busy} data-testid="crop-confirm" className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Apply crop
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
